import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { ToastService } from 'src/app/shared/utils/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PwaUpdateService {
  // Flag to prevent showing multiple update prompts
  private isPromptingUpdate = false;

  constructor(
    private swUpdate: SwUpdate,
    private toastService: ToastService,
    private translate: TranslateService
  ) {
    this.checkForUpdates();
  }

  private checkForUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      //console.log('Service worker not enabled');
      return;
    }

    // Check for updates every 30 minutes
    setInterval(() => {
      this.swUpdate.checkForUpdate().then(() => {
        //console.log('Checked for app updates');
      });
    }, 30 * 60 * 1000);

    // Listen for new versions
    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        this.promptUserToUpdate();
      });

    // Handle unrecoverable state
    this.swUpdate.unrecoverable.subscribe(event => {
      console.error('Service worker in unrecoverable state:', event.reason);
      this.notifyUnrecoverableState();
    });
  }

  private async promptUserToUpdate(): Promise<void> {
    // Prevent multiple prompts from showing
    if (this.isPromptingUpdate) {
      return;
    }
    this.isPromptingUpdate = true;

    try {
      // Wait for translations to be loaded before showing prompt
      // This ensures we don't show generic keys when translations haven't loaded yet
      const translations = await this.ensureTranslationsLoaded();
      
      const message = translations.updateAvailable;
      const action = translations.updateNow;
      
      // You could also use a more sophisticated dialog here
      if (confirm(`${message}\n\n${action}?`)) {
        this.updateApp();
      }
    } finally {
      this.isPromptingUpdate = false;
    }
  }

  /**
   * Ensures translations are loaded before proceeding.
   * Returns the translated strings, with fallbacks if translations fail to load.
   */
  private async ensureTranslationsLoaded(): Promise<{ updateAvailable: string; updateNow: string }> {
    const keys = ['PWA.UPDATE_AVAILABLE', 'PWA.UPDATE_NOW'];
    const fallbacks = {
      updateAvailable: 'A new version of the app is available',
      updateNow: 'Update now'
    };

    try {
      // Wait for the onLangChange or onTranslationChange to ensure translations are loaded
      // Use a timeout to prevent indefinite waiting
      const translationPromise = firstValueFrom(this.translate.get(keys));
      const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000));
      
      const result = await Promise.race([translationPromise, timeoutPromise]);
      
      if (result && result['PWA.UPDATE_AVAILABLE'] !== 'PWA.UPDATE_AVAILABLE') {
        return {
          updateAvailable: result['PWA.UPDATE_AVAILABLE'],
          updateNow: result['PWA.UPDATE_NOW']
        };
      }
    } catch {
      console.warn('PWA: Could not load translations, using defaults');
    }

    // Return fallbacks if translations couldn't be loaded
    return fallbacks;
  }

  private updateApp(): void {
    this.swUpdate.activateUpdate().then(() => {
      window.location.reload();
    });
  }

  private notifyUnrecoverableState(): void {
    const message = this.translate.instant('PWA.UNRECOVERABLE_STATE');
    this.toastService.showToast(message, 'error', 10000);
  }

  // Public method to manually check for updates
  public checkForUpdate(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.checkForUpdate();
    }
  }
}
